const optionSelector = [
  '.p-select-option',
  '.p-autocomplete-option',
  '.p-multiselect-option',
  '.p-select-option-group',
  '.p-autocomplete-option-group',
  '.p-multiselect-option-group',
].join(',');

const pixels = (value) => Number.parseFloat(value) || 0;

// Virtual rows have an assigned height, so measure their content instead of
// feeding that assigned height back into the next itemSize calculation.
const intrinsicOptionHeight = (option) => {
  const style = window.getComputedStyle(option);
  const padding = pixels(style.paddingTop) + pixels(style.paddingBottom);
  const borders = pixels(style.borderTopWidth) + pixels(style.borderBottomWidth);
  let top = Infinity;
  let bottom = -Infinity;

  for (const child of option.childNodes) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const childStyle = window.getComputedStyle(child);
      if (
        childStyle.display === 'none' ||
        childStyle.position === 'absolute' ||
        childStyle.position === 'fixed'
      ) {
        continue;
      }
      const rect = child.getBoundingClientRect();
      if (!rect.width && !rect.height) continue;
      const childBorders = pixels(childStyle.borderTopWidth) + pixels(childStyle.borderBottomWidth);
      const height = Math.max(rect.height, (child.scrollHeight || 0) + childBorders);
      top = Math.min(top, rect.top - pixels(childStyle.marginTop));
      bottom = Math.max(bottom, rect.top + height + pixels(childStyle.marginBottom));
    } else if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
      const range = option.ownerDocument.createRange();
      range.selectNodeContents(child);
      const rects = [...range.getClientRects()].filter((rect) => rect.width && rect.height);
      if (!rects.length) continue;
      const rect = range.getBoundingClientRect();
      const lineHeight = pixels(style.lineHeight) || pixels(style.fontSize) * 1.2;
      const height = Math.max(rect.height, rects.length * lineHeight);
      top = Math.min(top, rect.top);
      bottom = Math.max(bottom, rect.top + height);
    }
  }

  const content = Number.isFinite(top) && Number.isFinite(bottom) ? bottom - top : 0;
  return Math.ceil(Math.max(pixels(style.minBlockSize), content + padding + borders));
};

export function createTouchPickerOptionSizer() {
  let overlay = null;
  let onSize = null;
  let frame = null;
  let largestSize = 0;
  let resizeObserver = null;
  let mutationObserver = null;
  const observed = new Set();

  const measure = () => {
    frame = null;
    if (!overlay?.isConnected) return;
    let nextSize = largestSize;
    overlay.querySelectorAll(optionSelector).forEach((option) => {
      nextSize = Math.max(nextSize, intrinsicOptionHeight(option));
    });
    // Keep one stable height as virtual scrolling mounts and recycles options.
    // A shorter newly rendered option must not shrink rows already measured.
    if (nextSize <= largestSize) return;
    largestSize = nextSize;
    onSize?.(nextSize);
  };

  const schedule = () => {
    if (frame === null) frame = window.requestAnimationFrame(measure);
  };

  const observeContents = () => {
    if (!overlay || !resizeObserver) return;
    const elements = new Set([overlay]);
    overlay
      .querySelectorAll('[data-pc-section="list"], [data-pc-section="listcontainer"]')
      .forEach((element) => elements.add(element));
    overlay.querySelectorAll(optionSelector).forEach((option) => {
      elements.add(option);
      option.querySelectorAll('*').forEach((element) => elements.add(element));
    });
    observed.forEach((element) => {
      if (elements.has(element)) return;
      resizeObserver.unobserve(element);
      observed.delete(element);
    });
    elements.forEach((element) => {
      if (observed.has(element)) return;
      observed.add(element);
      resizeObserver.observe(element);
    });
  };

  const unbind = () => {
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    resizeObserver = null;
    mutationObserver = null;
    observed.clear();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    }
    frame = null;
    overlay = null;
    onSize = null;
    largestSize = 0;
  };

  return {
    bind(options) {
      unbind();
      if (
        !options?.overlay ||
        typeof options.onSize !== 'function' ||
        typeof window === 'undefined'
      ) {
        return;
      }
      overlay = options.overlay;
      onSize = options.onSize;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(schedule);
        observeContents();
      }
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          observeContents();
          schedule();
        });
        mutationObserver.observe(overlay, { childList: true, subtree: true, characterData: true });
      }
      window.addEventListener('resize', schedule);
      window.visualViewport?.addEventListener('resize', schedule);
      schedule();
    },
    unbind,
  };
}
